import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useConfigStore, useAppStore, useWindowStore } from '../stores';
import {
  getSettings,
  createContainerStyleMarkup,
  setDocumentStyleProperties,
  processFeatureFlags,
} from '../AppHelpers';
import variables from '../GlobalScss/variables.scss';
import useFathomExclusion from './useFathomExclusion';

const FATHOM_FILTERED_PLATFORMS = ['page', 'siteSee'];

/**
 * Custom hook to handle app settings, config, and initialization.
 * This includes:
 * - Parsing settings from URL/location
 * - Storing settings in app store
 * - Processing community config and feature flags
 * - Setting up container styles and CSS variables
 * - Window resize handling
 * - Page title updates
 * - Analytics script injection
 *
 * @param {Object} communityData - Community data from API
 * @returns {Object} Settings and container style
 */
const useAppSettings = (communityData) => {
  const [containerStyle, setContainerStyle] = useState(null);

  // Config store
  const config = useConfigStore((state) => state.config);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const setCommunityConfig = useConfigStore((state) => state.setCommunityConfig);

  // App store
  const setSettings = useAppStore((state) => state.setSettings);

  // Window store
  const setWindowDimensions = useWindowStore((state) => state.setWindowDimensions);

  const location = useLocation();
  const { pathname } = location;

  // Get settings from URL/location
  const settings = getSettings(window?.location?.host, location);

  const {
    platform,
    isPublic,
    urlSlug,
    isProduction,
  } = settings;

  const { communityName } = config || {};

  // Turn off scrollbar if this isn't the PIB.
  useEffect(() => {
    if (platform !== 'page') {
      window.document.body.classList.add('noScroll');
    }
  }, [platform]);

  // Store settings in app store
  useEffect(() => {
    setSettings(settings);
  }, [settings, setSettings]);

  // Set various config from community data.
  useEffect(() => {
    if (communityData) {
      const processedFeatureFlags = processFeatureFlags(
        platform,
        isPublic,
        communityData?.yt?.features,
      );

      setCommunityConfig(communityData, processedFeatureFlags);
    }

    // We want to use default colors if no colors are set.
    const primaryColor = communityData?.yt?.primaryColor || variables.primary;
    const secondaryColor = communityData?.yt?.secondaryColor || variables.secondar;

    setDocumentStyleProperties(
      window,
      primaryColor,
      secondaryColor,
    );

    const rawContainerStyle = createContainerStyleMarkup(
      primaryColor,
      secondaryColor,
    );

    setContainerStyle(rawContainerStyle);
  }, [platform, isPublic, communityData, setCommunityConfig]);

  // Listen for resize events and set window dims.
  useEffect(() => {
    setWindowDimensions(window.innerWidth, window.innerHeight);
    const handleResize = () => {
      setWindowDimensions(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setWindowDimensions]);

  // Update page title with community name.
  useEffect(() => {
    if (communityName) {
      document.title = `${communityName} | YourTour`;
    }
  }, [communityName]);

  // PIB and /tourguide gate Fathom on an IP-based exclusion verdict from
  // Drupal so admin/sales-staff visits don't inflate per-PIB analytics.
  // Other platforms load Fathom unconditionally. The verdict resolves in
  // every env (so e2e can exercise it); only the script load is prod-gated.
  const isFathomFilteredPlatform = FATHOM_FILTERED_PLATFORMS.includes(platform);
  const { excluded: fathomExcluded, ready: fathomReady } = useFathomExclusion({
    enabled: isFathomFilteredPlatform,
    platform,
  });

  useEffect(() => {
    // ?fathomTest=1 forces Fathom to load in any env so the exclusion gate
    // can be verified on a dev server. Sticky for the tab via sessionStorage.
    const params = new URLSearchParams(window.location.search);
    if (params.get('fathomTest') === '1') {
      sessionStorage.setItem('ytFathomTestMode', '1');
    }
    const fathomForced = sessionStorage.getItem('ytFathomTestMode') === '1';

    if (!isProduction && !fathomForced) return undefined;
    if (isFathomFilteredPlatform && (!fathomReady || fathomExcluded)) return undefined;

    const head = document.querySelector('head');
    const script = document.createElement('script');
    script.setAttribute('src', 'https://cdn.usefathom.com/script.js');
    script.setAttribute('data-spa', 'auto');
    script.setAttribute('data-site', 'ZLWPSRJO');
    script.setAttribute('defer', 'defer');
    head.appendChild(script);

    return () => {
      if (script.parentNode === head) {
        head.removeChild(script);
      }
    };
  }, [isProduction, isFathomFilteredPlatform, fathomReady, fathomExcluded]);

  return {
    settings,
    containerStyle,
    featureFlags,
    pathname,
  };
};

export default useAppSettings;
